/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/app/config/navPermissions.ts
 * layer: web
 * area: config
 * kind: source
 * web_root: config
 * relpath: web/src/app/config/navPermissions.ts
 * filename: navPermissions.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

// Navigation permissions and role-based access control.
// Roles and routes stay as data; the guard composes all assigned roles at runtime.

export type Role =
  | 'admin'
  | 'owner'
  | 'manager'
  | 'sdm'
  | 'dispatcher'
  | 'engineer'
  | 'engineer_l3'
  | 'engineer_l2'
  | 'engineer_l1'
  | 'engineer_l0'
  | 'account_manager'
  | 'csm'
  | 'finance'
  | 'billing'
  | 'user'
  | 'readonly'
  | 'viewer';

export type PermissionAction = 'create' | 'read' | 'update' | 'delete';
export type PermissionResource =
  | 'app'
  | 'automation'
  | 'billing'
  | 'customer-success'
  | 'customers'
  | 'design'
  | 'devices'
  | 'dispatch'
  | 'library'
  | 'monitoring'
  | 'platform'
  | 'procurement'
  | 'reports'
  | 'runway'
  | 'sales'
  | 'vault';
export type Permission = `${PermissionResource}:${PermissionAction}`;

export interface RolePolicy {
  permissions: ReadonlySet<Permission>;
  /** Route prefixes denied even when another assigned role grants the permission. */
  exclusions: readonly string[];
}

export interface AccessSubject {
  roles: readonly string[];
  /** Optional server-supplied grants support custom roles without a frontend deploy. */
  permissions?: readonly string[];
  /** Optional server-supplied route denies. Deny always wins. */
  exclusions?: readonly string[];
}

export interface UserAccessData {
  role?: string;
  roles?: readonly string[];
  permissions?: readonly string[];
  routeExclusions?: readonly string[];
}

export interface RouteAccessDecision {
  allowed: boolean;
  reason: 'allowed' | 'excluded' | 'missing-permission' | 'no-subject';
  requiredPermission: Permission;
}

const ACTIONS: readonly PermissionAction[] = ['create', 'read', 'update', 'delete'];
const RESOURCES: readonly PermissionResource[] = [
  'app',
  'automation',
  'billing',
  'customer-success',
  'customers',
  'design',
  'devices',
  'dispatch',
  'library',
  'monitoring',
  'platform',
  'procurement',
  'reports',
  'runway',
  'sales',
  'vault',
];

function grants(...permissions: Permission[]): ReadonlySet<Permission> {
  return new Set(permissions);
}

function resourceGrants(
  resources: readonly PermissionResource[],
  actions: readonly PermissionAction[] = ACTIONS
): Permission[] {
  return resources.flatMap((resource) =>
    actions.map((action): Permission => `${resource}:${action}`)
  );
}

const APP_READ = 'app:read' satisfies Permission;
const TECHNICAL_READ = resourceGrants(['devices', 'vault'], ['read']);
const TECHNICAL_OPERATE = resourceGrants(
  ['automation', 'devices', 'library', 'monitoring', 'vault'],
  ['create', 'read', 'update']
);
/**
 * Runway capabilities (J6-1, J6-3):
 *   runway:read   — open Runway: runs, hand-offs (accepting one is governed by
 *                   the hand-off's own to_role on the BE)
 *   runway:create — start (launch) a runway; BE operator guard = every
 *                   engineer tier, dispatcher, sdm, manager, admin tier
 *   runway:update — author templates (create, edit, publish); L2 and above
 * The roles holding runway:read must equal the BE "runway_viewer" guard
 * (hyperion-be pkg/middleware/roles.go), which filters hand-off recipients.
 */
const RUNWAY_RUN = resourceGrants(['runway'], ['read', 'create']);
const RUNWAY_AUTHOR = resourceGrants(['runway'], ['create', 'read', 'update']);
/** Every engineer tier can watch Monitoring and read the Library (J6-1, J4-1). */
const ENGINEER_BASELINE = [APP_READ, ...TECHNICAL_READ, 'monitoring:read', 'library:read'] as const;
const COMMERCIAL_OPERATE = resourceGrants(
  ['customer-success', 'customers', 'sales'],
  ['create', 'read', 'update']
);
const SERVICE_MANAGEMENT = resourceGrants(
  [
    'automation',
    'customer-success',
    'customers',
    'devices',
    'dispatch',
    'monitoring',
    'reports',
    'runway',
  ],
  ['create', 'read', 'update']
);

/**
 * Built-in roles are fallback data. Server-supplied permissions can extend this
 * table for tenant-defined roles, and users may compose any number of roles.
 */
export const ROLE_POLICIES: ReadonlyMap<Role, RolePolicy> = new Map([
  ['owner', { permissions: grants(...resourceGrants(RESOURCES)), exclusions: [] }],
  ['admin', { permissions: grants(...resourceGrants(RESOURCES)), exclusions: [] }],
  [
    'manager',
    {
      permissions: grants(
        APP_READ,
        ...SERVICE_MANAGEMENT,
        ...COMMERCIAL_OPERATE,
        'procurement:read'
      ),
      exclusions: [],
    },
  ],
  ['sdm', { permissions: grants(APP_READ, ...SERVICE_MANAGEMENT), exclusions: [] }],
  [
    'dispatcher',
    {
      permissions: grants(
        APP_READ,
        ...resourceGrants(['dispatch'], ACTIONS),
        ...resourceGrants(['devices', 'monitoring', 'reports'], ['read']),
        // Dispatchers launch and track runways; authoring is L2+ (J6-1).
        ...RUNWAY_RUN
      ),
      exclusions: [],
    },
  ],
  [
    'engineer_l3',
    {
      permissions: grants(
        APP_READ,
        ...TECHNICAL_OPERATE,
        'design:read',
        ...resourceGrants(['reports'], ['read']),
        ...RUNWAY_AUTHOR
      ),
      exclusions: [],
    },
  ],
  [
    'engineer_l2',
    { permissions: grants(APP_READ, ...TECHNICAL_OPERATE, ...RUNWAY_AUTHOR), exclusions: [] },
  ],
  // The BE treats plain "engineer" as L2 (core/user.RoleEngineer).
  [
    'engineer',
    { permissions: grants(APP_READ, ...TECHNICAL_OPERATE, ...RUNWAY_AUTHOR), exclusions: [] },
  ],
  [
    'engineer_l1',
    {
      // Phone-first L1: watch Monitoring, read the Library, run runways and
      // accept hand-offs — but not author them.
      permissions: grants(
        ...ENGINEER_BASELINE,
        ...resourceGrants(['devices'], ['update']),
        ...RUNWAY_RUN
      ),
      exclusions: [],
    },
  ],
  ['engineer_l0', { permissions: grants(...ENGINEER_BASELINE, 'runway:read'), exclusions: [] }],
  [
    'account_manager',
    {
      permissions: grants(APP_READ, ...COMMERCIAL_OPERATE, 'reports:read', 'runway:read'),
      exclusions: [],
    },
  ],
  [
    'csm',
    {
      permissions: grants(APP_READ, ...COMMERCIAL_OPERATE, 'reports:read', 'runway:read'),
      exclusions: [],
    },
  ],
  [
    'finance',
    {
      permissions: grants(
        APP_READ,
        ...resourceGrants(['billing', 'procurement', 'reports'], ['read', 'update']),
        'sales:read'
      ),
      exclusions: [],
    },
  ],
  [
    'billing',
    {
      permissions: grants(
        APP_READ,
        ...resourceGrants(['billing', 'procurement', 'reports'], ['read', 'update']),
        'sales:read'
      ),
      exclusions: [],
    },
  ],
  // Legacy alias of engineer_l1. The BE excludes it from the engineer guard
  // and from runway_viewer, so it only gains the read-only Monitoring view.
  ['user', { permissions: grants(APP_READ, ...TECHNICAL_READ, 'monitoring:read'), exclusions: [] }],
  [
    'readonly',
    {
      permissions: grants(APP_READ, 'library:read', 'reports:read'),
      exclusions: ['/library/builders/report'],
    },
  ],
  ['viewer', { permissions: grants(APP_READ), exclusions: [] }],
]);

export const BUILT_IN_ROLES: readonly Role[] = [...ROLE_POLICIES.keys()];

/**
 * Exact and prefix route policies share one keyed table. Nested paths walk
 * their parent segments, so lookup cost follows URL depth rather than table size.
 */
export const ROUTE_PERMISSIONS: ReadonlyMap<string, Permission> = new Map([
  ['/platform', 'platform:read'],
  ['/switchboard/configure', 'platform:read'],
  ['/admin', 'platform:read'],
  ['/tenant-setup', 'platform:read'],
  ['/design', 'design:read'],
  ['/reports', 'reports:read'],
  ['/billing', 'billing:read'],
  ['/profitability', 'sales:read'],
  ['/devices', 'devices:read'],
  ['/remote', 'devices:read'],
  ['/automation', 'automation:read'],
  ['/monitoring', 'monitoring:read'],
  ['/workbench/monitoring', 'monitoring:read'],
  ['/patches', 'devices:read'],
  ['/vault', 'vault:read'],
  ['/dispatch', 'dispatch:read'],
  ['/proposals', 'sales:read'],
  ['/contracts', 'sales:read'],
  ['/customer-lifecycle', 'customer-success:read'],
  ['/procurement', 'procurement:read'],
  ['/runway', 'runway:read'],
  ['/runway/new', 'runway:create'],
  ['/runway/templates/:id/edit', 'runway:update'],
  ['/library', 'library:read'],
  ['/library/builders/:kind/:id', 'library:update'],
  ['/tickets', APP_READ],
  ['/operations', APP_READ],
  ['/workbench', APP_READ],
  ['/portfolio', APP_READ],
  ['/switchboard', APP_READ],
]);

const DYNAMIC_ROUTE_KEYS: readonly { pattern: RegExp; key: string }[] = [
  { pattern: /^\/runway\/templates\/[^/]+\/edit$/, key: '/runway/templates/:id/edit' },
  {
    pattern: /^\/library\/builders\/[^/]+\/[^/]+$/,
    key: '/library/builders/:kind/:id',
  },
];

function normalizedPath(route: string): string {
  const path = route.split(/[?#]/, 1)[0] || '/';
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

function dynamicRouteKey(path: string): string | undefined {
  return DYNAMIC_ROUTE_KEYS.find(({ pattern }) => pattern.test(path))?.key;
}

function requiredPermissionForRoute(route: string): Permission {
  const path = normalizedPath(route);
  const dynamicKey = dynamicRouteKey(path);
  if (dynamicKey) return ROUTE_PERMISSIONS.get(dynamicKey) ?? APP_READ;

  let candidate = path;
  while (candidate) {
    const permission = ROUTE_PERMISSIONS.get(candidate);
    if (permission) return permission;
    const slash = candidate.lastIndexOf('/');
    if (slash <= 0) break;
    candidate = candidate.slice(0, slash);
  }
  return APP_READ;
}

function routeMatchesExclusion(route: string, exclusion: string): boolean {
  const path = normalizedPath(route);
  const denied = normalizedPath(exclusion);
  const routeKey = dynamicRouteKey(path);
  return path === denied || path.startsWith(`${denied}/`) || routeKey === denied;
}

export function subjectForUser(user: UserAccessData | null | undefined): AccessSubject {
  if (!user) return { roles: [] };
  return {
    roles: [...new Set([user.role, ...(user.roles ?? [])].filter(Boolean) as string[])],
    permissions: user.permissions,
    exclusions: user.routeExclusions,
  };
}

function permissionsForSubject(subject: AccessSubject): Set<string> {
  const permissions = new Set(subject.permissions ?? []);
  for (const role of subject.roles) {
    const policy = ROLE_POLICIES.get(role as Role);
    for (const permission of policy?.permissions ?? []) permissions.add(permission);
  }
  return permissions;
}

function exclusionsForSubject(subject: AccessSubject): string[] {
  return [
    ...(subject.exclusions ?? []),
    ...subject.roles.flatMap((role) => ROLE_POLICIES.get(role as Role)?.exclusions ?? []),
  ];
}

/** Pure deny-first authorization decision used by every protected route. */
export function evaluateRouteAccess(subject: AccessSubject, route: string): RouteAccessDecision {
  const requiredPermission = requiredPermissionForRoute(route);
  const hasIdentity = subject.roles.length > 0 || (subject.permissions?.length ?? 0) > 0;
  if (!hasIdentity) {
    return { allowed: false, reason: 'no-subject', requiredPermission };
  }

  if (exclusionsForSubject(subject).some((exclusion) => routeMatchesExclusion(route, exclusion))) {
    return { allowed: false, reason: 'excluded', requiredPermission };
  }

  if (!permissionsForSubject(subject).has(requiredPermission)) {
    return { allowed: false, reason: 'missing-permission', requiredPermission };
  }
  return { allowed: true, reason: 'allowed', requiredPermission };
}

export function hasPermission(subject: AccessSubject, permission: Permission): boolean {
  return permissionsForSubject(subject).has(permission);
}

/** Boolean convenience wrapper for navigation filtering and route components. */
export function canAccessRoute(subject: AccessSubject, route: string): boolean {
  return evaluateRouteAccess(subject, route).allowed;
}

/** Built-in personas that can enter a route without server-supplied custom grants. */
export function builtInRolesForRoute(route: string): Role[] {
  return BUILT_IN_ROLES.filter((role) => canAccessRoute({ roles: [role] }, route));
}

// Default favourites by role.
const ROLE_DEFAULTS: Record<string, string[]> = {
  // Owner / Admin — high-level overview + settings
  owner: [
    '/workbench',
    '/operations',
    '/tickets',
    '/devices',
    '/portfolio',
    '/billing',
    '/reports',
    '/switchboard',
  ],
  admin: [
    '/workbench',
    '/operations',
    '/tickets',
    '/devices',
    '/portfolio',
    '/billing',
    '/reports',
    '/switchboard',
  ],

  // SDM / Manager — runs the service desk
  sdm: [
    '/workbench',
    '/operations',
    '/tickets',
    '/dispatch',
    '/devices',
    '/portfolio',
    '/reports',
    '/workitems',
  ],
  manager: [
    '/workbench',
    '/operations',
    '/tickets',
    '/dispatch',
    '/devices',
    '/portfolio',
    '/reports',
    '/workitems',
  ],

  // Dispatcher — routes work
  dispatcher: ['/workbench', '/dispatch', '/tickets', '/devices', '/calls'],

  // Engineer L3 — senior/lead, broad technical + oversight
  engineer_l3: [
    '/workbench',
    '/tickets',
    '/devices',
    '/remote',
    '/library/scripts',
    '/scripts',
    '/monitoring',
    '/patches',
    '/workitems',
    '/vault',
  ],

  // Engineer L2 / standard — day-to-day technical
  engineer_l2: [
    '/workbench',
    '/tickets',
    '/devices',
    '/remote',
    '/vault',
    '/library/scripts',
    '/scripts',
    '/backups',
    '/patches',
  ],
  engineer: [
    '/workbench',
    '/tickets',
    '/devices',
    '/remote',
    '/vault',
    '/library/scripts',
    '/scripts',
    '/backups',
    '/patches',
  ],

  // Engineer L1 / user — junior technical
  engineer_l1: ['/workbench', '/tickets', '/devices', '/remote', '/vault', '/backups'],
  user: ['/workbench', '/tickets', '/devices', '/remote', '/vault', '/backups'],

  // Engineer L0 — trainee
  engineer_l0: ['/workbench', '/tickets', '/devices', '/remote', '/vault'],

  // Account Manager — CRM is home
  account_manager: [
    '/workbench',
    '/portfolio',
    '/proposals',
    '/contracts',
    '/tickets',
    '/profitability',
    '/customer-lifecycle/engagement',
  ],

  // CSM — customer success
  csm: [
    '/workbench',
    '/portfolio',
    '/contracts',
    '/qbr',
    '/tickets',
    '/customer-lifecycle/engagement',
  ],

  // Finance / Billing
  finance: ['/workbench', '/billing', '/contracts', '/profitability', '/reports', '/procurement'],
  billing: ['/workbench', '/billing', '/contracts', '/profitability', '/reports', '/procurement'],

  // Readonly
  readonly: ['/workbench', '/reports'],
};

/** Get default favourite hrefs for a role. */
export function getDefaultFavourites(role: string): string[] {
  return ROLE_DEFAULTS[role] ?? ROLE_DEFAULTS.readonly;
}
